import React, {useState} from 'react';
import {t} from 'i18next';
import {useFetchNewestProblemsQuery} from '@goproblems/shared/api/indexPage';
import CollectionProgressCardSkeleton from '@components/skeleton/CollectionProgressCardSkeleton';
import useNumberOfItemsPerRow from '@utils/hooks/useNumberOfItemsPerRow';
import IndexSectionHeading from './IndexSectionHeading';
import Rank from '@components/common/RankText';
import {formatDistance} from 'date-fns';
import {ProblemV2} from '@types';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheckCircle, faCircleXmark} from '@fortawesome/free-solid-svg-icons';
import {Link} from 'react-router';
import {ThemeAwareSkeleton} from '@components/theme/ThemeAwareSkeleton';
import {ThemeAwareBadge} from '@components/theme/ThemeAwareBadge';
import {UserIdentity} from '@components/common/UserIdentity';
import {useTranslation} from 'react-i18next';

const NewestProblemCard: React.FC<{
    offset: number;
    newProblemItem: ProblemV2;
}> = ({newProblemItem, offset}) => {
    const {t} = useTranslation();
    const [imageLoaded, setImageLoaded] = useState(false);

    const solvedClass = () => {
        if (newProblemItem.tries + newProblemItem.solves > 0) {
            return newProblemItem.solves > 0
                ? 'border-success'
                : 'border-danger';
        }
        return '';
    };

    return (
        <div className={`card relative ${solvedClass()}`}>
            <span className="absolute inset-x-0 top-0 z-10 m-1 flex items-stretch justify-between gap-1">
                <ThemeAwareBadge className="!inline-flex items-center justify-center">
                    <Rank rank={newProblemItem.rank} />
                </ThemeAwareBadge>
                {newProblemItem.tries + newProblemItem.solves > 0 && (
                    <ThemeAwareBadge className="!inline-flex items-center justify-center">
                        <FontAwesomeIcon
                            icon={
                                newProblemItem.solves > 0
                                    ? faCheckCircle
                                    : faCircleXmark
                            }
                            className={
                                newProblemItem.solves > 0
                                    ? 'text-success'
                                    : 'text-red-600 dark:text-red-400'
                            }
                        />
                    </ThemeAwareBadge>
                )}
            </span>
            <div>
                <Link
                    to={`/problems/${newProblemItem.id}?name=newest&offset=${offset}`}
                    className="text-decoration-none">
                    {!imageLoaded && (
                        <ThemeAwareSkeleton
                            style={{
                                width: '100%',
                                aspectRatio: 1.02,
                            }}
                        />
                    )}
                    <img
                        onLoad={() => setImageLoaded(true)}
                        className="card-img-top"
                        style={{display: imageLoaded ? 'block' : 'none'}}
                        src={newProblemItem.imageUrl}
                        alt={t('common.board_thumbnail')}
                    />
                </Link>

                <div className="card-body index-home-problem-meta">
                    <span className="index-home-problem-date">
                        {formatDistance(
                            new Date(newProblemItem.createdAt),
                            new Date(),
                            {addSuffix: true}
                        )}
                    </span>
                    <UserIdentity
                        user={newProblemItem.author}
                        avatarSize={20}
                        className="index-home-problem-author"
                    />
                </div>
            </div>
        </div>
    );
};

const NewestProblem: React.FC = () => {
    const {
        data: collectionProgressEntries,
        isLoading,
        isError,
    } = useFetchNewestProblemsQuery();
    const numberOfItems = useNumberOfItemsPerRow();

    // Determine the number of placeholder items needed
    const placeholders = collectionProgressEntries
        ? Math.max(0, numberOfItems - collectionProgressEntries.length)
        : 0;

    if (!isLoading && collectionProgressEntries?.length === 0) return null;

    if (isError) {
        return (
            <div>{t('index.section_load_error')}</div>
        );
    }

    const renderVisibilityClasses = (index: number): string => {
        if (index >= 8) return 'hidden site-xxl:block';
        if (index >= 6) return 'hidden site-xl:block';
        if (index >= 4) return 'hidden md:block';
        if (index >= 2) return 'hidden site-sm:block';
        return '';
    };

    const renderSkeletons = () =>
        Array.from({length: numberOfItems}).map(
            (_, index) => (
                <div
                    key={`skeleton-${index}`}
                    className={`min-w-0 flex-1 ${renderVisibilityClasses(index)}`}>
                    <CollectionProgressCardSkeleton />
                </div>
            )
        );

    const renderProblems = () =>
        collectionProgressEntries
            ?.slice(0, numberOfItems)
            .map((problem, index) => (
                <div
                    key={problem.id}
                    className={`min-w-0 flex-1 ${renderVisibilityClasses(index)}`}>
                    <NewestProblemCard offset={index} newProblemItem={problem} />
                </div>
            ));

    const renderPlaceholders = () =>
        Array.from({length: placeholders}).map((_, index) => (
            <div
                key={`placeholder-${index}`}
                className={`min-w-0 flex-1 ${renderVisibilityClasses(index)}`}
            />
        ));

    return (
        <div className="index-home-feature index-home-card-list w-full">
            <IndexSectionHeading
                title={t('index.newest_problems')}
                to="/problems"
            />
            <div className="flex flex-nowrap gap-2">
                {isLoading ? (
                    renderSkeletons()
                ) : (
                    <>
                        {renderProblems()}
                        {renderPlaceholders()}
                    </>
                )}
            </div>
        </div>
    );
};

export default NewestProblem;
